import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';

import { useAtomValue } from 'jotai';
import { useSonner } from 'sonner';

import { useAppDisplayModeContext } from '@archipro-design/aria';

import { footerElementAtom } from '~/state/project-detail-states';
import { contentGateElementAtom } from '~/modules-rocco/root/states/ui-state-content-gate-in-view';
import { islandBerthTop } from './use-section-reaches-island';
import type { IslandMode, IslandRegistration } from './types';

interface RegisterInput {
    id: symbol;
    priority: number;
    mode: IslandMode;
    el: HTMLElement | null;
}

type RegistrationPatch = Partial<
    Pick<IslandRegistration, 'mode' | 'priority' | 'el'>
>;

interface MagicIslandContextValue {
    register: (input: RegisterInput) => void;
    update: (id: symbol, patch: RegistrationPatch) => void;
    unregister: (id: symbol) => void;
    /**
     * Force the island to a mode regardless of scroll position, or `null` to
     * release it back to the scroll-driven sections. Used by always-on surfaces
     * like Bulk Enquire that must show while any item is selected. The override
     * wins over every registered section.
     */
    setOverride: (mode: IslandMode | null) => void;
    /**
     * The mode shown when NO scrolled section claims the reading line (instead
     * of hiding), or `null` for the default hide. Used for the persistent
     * purchase pill. A section may still blank the island explicitly by
     * registering a `hidden` mode — that claim wins over the fallback (e.g.
     * the purchase block suppresses the pill while its own CTA is on screen).
     */
    setFallback: (mode: IslandMode | null) => void;
    /** The content the shell should currently render (derived). */
    activeMode: IslandMode;
}

const MagicIslandContext = createContext<MagicIslandContextValue | null>(null);

/** Null when there is no provider (feature off) — section hooks then no-op. */
export const useMagicIslandController = () => useContext(MagicIslandContext);

const HIDDEN: IslandMode = { kind: 'hidden' };

// The IntersectionObserver respects ancestor clipping, but the manual band
// checks below read raw getBoundingClientRect()s — which ignore it. Content
// force-mounted inside a collapsed accordion row keeps its natural height
// while an overflow-hidden, zero-height wrapper clips it; the raw rect then
// "contains" the reading line and phantom-claims the island. Intersect the
// rect with every vertically-clipping ancestor so the checks see what the
// reader sees. Only `overflow-y` matters here — the band test is vertical,
// and an `overflow-x`-only clipper (the pages' `overflow-x-clip` wrappers)
// leaves vertical overflow visible. Returns null when nothing of the element
// is actually visible.
//
// The ancestor walk reads getComputedStyle per node, so callers pre-filter
// with the raw rect first: a clipped rect is always a subset of the raw one,
// so an element whose raw rect misses the band can be rejected without the
// walk — which keeps the per-frame cost to the one or two sections actually
// near the reading line.
const clippedRect = (el: HTMLElement, rect: DOMRect): DOMRect | null => {
    if (rect.height === 0) return null;
    let clipped = rect;
    let node = el.parentElement;
    while (node) {
        if (getComputedStyle(node).overflowY !== 'visible') {
            const bounds = node.getBoundingClientRect();
            const top = Math.max(clipped.top, bounds.top);
            const bottom = Math.min(clipped.bottom, bounds.bottom);
            if (bottom <= top) return null;
            clipped = new DOMRect(clipped.x, top, clipped.width, bottom - top);
        }
        node = node.parentElement;
    }
    return clipped;
};

// A horizontal "reading line" at this fraction of the viewport height (0 = top,
// 1 = bottom). A section owns the island only while the line is inside its box —
// the moment you scroll the line past its top or bottom edge it lets go. So a
// section you have scrolled away from (e.g. the gallery, mostly off the top)
// never keeps the island, and there is nothing to "share" with neighbours: at
// most one section contains the line.
//
// This is deliberately NOT a visible-area ratio. A ratio of "visible / min(own
// height, viewport)" measures different things for short vs tall sections — for
// anything taller than the viewport it becomes "how much of the viewport does
// this section cover", so a tall block (e.g. the Similar Products grid) pins at
// ~100% across its whole extent and refuses to release. Line-containment is
// size-independent and direction-symmetric: tall and short sections behave the
// same, and scrolling up releases at the same edge scrolling down revealed at.
const TRIGGER_FRACTION = 0.5;

// The observer watches a thin band (this % of viewport height) around the line
// rather than a zero-height line. A zero-area intersection is unreliable — some
// browsers drop the "leaving" notification, stranding a section in the active
// set — so the band gives the intersection real area and makes enter/leave
// fire cleanly. `pickActive` still narrows to the exact line, so the band only
// affects observer bookkeeping, not when a section actually claims the island.
const TRIGGER_BAND_PCT = 6;

// Mobile parks the island until the page has scrolled past this many viewport
// heights. The mobile pages keep their real CTAs inside the first screenful
// (the product page's purchase block, for one), and the island's berth sits
// right on top of them — so a pill popping up on landing covers the very action
// it duplicates. Arming only once the reader is past the first screenful keeps
// the initial view clean; scrolling back under the line parks it again. Desktop
// is unaffected (its CTAs live clear of the island's berth). The always-on
// override (e.g. Bulk Enquire) bypasses the gate — it must show the moment
// items are selected, wherever the page sits.
const MOBILE_ARM_VIEWPORTS = 1.1;

// Revealing is instant, but hiding waits this long: a brief gap between two
// registered blocks, or a momentary shift while a carousel / accordion settles,
// would otherwise drop the whole pill out and animate it straight back in a few
// pixels later. The grace period bridges those so the island stops blinking
// during a continuous scroll; only genuinely leaving every section (grace
// elapses with nothing reclaiming it) hides it.
const HIDE_GRACE_MS = 280;

/**
 * The Magic Island controller. One IntersectionObserver collapses the viewport
 * root to the reading line above (its top/bottom rootMargin insets sum to
 * -100%), so each registered section reports `isIntersecting` exactly while that
 * line is inside its box. The active section is the one currently crossing the
 * line — innermost (greatest top) when registrations nest, with `priority` as an
 * override. The browser watches scroll, resize and layout reflow off the main
 * thread, so there is no scroll listener, rAF loop or ResizeObserver, and
 * sections never re-render on scroll. Page-agnostic.
 */
export const MagicIslandProvider = ({ children }: { children: ReactNode }) => {
    const registry = useRef<Map<symbol, IslandRegistration>>(new Map());
    // The observed element → its registration id, for the observer callback.
    const targets = useRef<Map<Element, symbol>>(new Map());
    // Ids whose box currently crosses the reading line (kept live by the observer).
    const intersecting = useRef<Set<symbol>>(new Set());
    const observerRef = useRef<IntersectionObserver | null>(null);
    const activeIdRef = useRef<symbol | null>(null);
    const hideTimerRef = useRef(0);
    // The scroll-driven winner. The rendered mode is `override ?? pickedMode`.
    const [pickedMode, setPickedMode] = useState<IslandMode>(HIDDEN);
    // An always-on mode that takes precedence over the scroll-driven sections.
    const [overrideMode, setOverrideMode] = useState<IslandMode | null>(null);
    // Shown when no section claims the line (instead of hiding). Null = hide.
    const [fallbackMode, setFallbackMode] = useState<IslandMode | null>(null);
    // True while a `hidden`-mode registration claims the line — an explicit
    // blank that must beat the fallback, unlike the no-claim hidden state.
    const [suppressed, setSuppressed] = useState(false);
    // The mobile arm gate (see MOBILE_ARM_VIEWPORTS). Desktop is always armed.
    const { desktop } = useAppDisplayModeContext();
    const [armed, setArmed] = useState(desktop);
    // True once the site footer has risen into the island's berth: the reader is
    // at the end of the page and the pill would be floating over the footer.
    const [atFooter, setAtFooter] = useState(false);
    // AppPage publishes the one site-wide footer here, so every island host gets
    // this bound without wiring a "last content block" ref per page.
    const footerElement = useAtomValue(footerElementAtom);
    // The content gate is a second bound of the same kind: past it the page is
    // blurred out behind a sign-in wall, and a pill floating over that offers
    // actions on tiles the reader can no longer reach.
    const contentGateElement = useAtomValue(contentGateElementAtom);
    // The site-wide toast docks at the island's exact berth and wears the same
    // glass, so while any toast is visible the island yields — otherwise the
    // two pills stack into a double glass. The island's usual surface-swap
    // exit plus the toast's matching spring entry read as one island
    // transition, and the island springs back once the last toast is gone
    // (sonner drops a toast from this list as its exit starts).
    const { toasts } = useSonner();
    const toastHoldsBerth = toasts.length > 0;

    // Re-measured on every scroll frame (React bails when the boolean holds),
    // and once on mount so a deep link restored mid-page starts armed.
    const syncArmed = useCallback(() => {
        if (desktop) return;
        setArmed(window.scrollY > MOBILE_ARM_VIEWPORTS * window.innerHeight);
    }, [desktop]);

    // Re-measured on the same rAF-throttled scroll frame as the arm gate. The
    // footer's top edge is the bottom of the page's last content block, so this
    // is the same test `useSectionReachesIsland` applies to a section — read off
    // the footer instead, which every page has.
    const syncAtFooter = useCallback(() => {
        const bounds = [footerElement, contentGateElement].filter(
            (el): el is HTMLElement => !!el,
        );
        if (!bounds.length) {
            setAtFooter(false);
            return;
        }
        setAtFooter(
            bounds.some(
                (el) => el.getBoundingClientRect().top <= islandBerthTop(),
            ),
        );
    }, [footerElement, contentGateElement]);

    // Pick the active section among those crossing the line. Reads layout for
    // only those few elements. Callers also run this on `update` so a live mode
    // change (pagination index, "Load more" → "Loading…") reaches the shell even
    // though it triggers no intersection change.
    const pickActive = useCallback(() => {
        const viewportHeight = window.innerHeight || 1;
        const bandHalf = ((TRIGGER_BAND_PCT / 100) * viewportHeight) / 2;
        const bandTop = TRIGGER_FRACTION * viewportHeight - bandHalf;
        const bandBottom = TRIGGER_FRACTION * viewportHeight + bandHalf;
        let best: IslandRegistration | null = null;
        let bestKey = -Infinity;
        for (const id of intersecting.current) {
            const reg = registry.current.get(id);
            // `hidden`-mode registrations stay in the running: they claim the
            // line like any section, and a winning hidden claim blanks the
            // island (suppressing the fallback) rather than being ignored.
            if (!reg?.el) {
                intersecting.current.delete(id);
                continue;
            }
            // Authoritative re-check against the SAME clip-aware measure the
            // observer uses: prune anything that has actually left the band.
            // A dropped "leaving" notification can then never strand a section
            // in the active set, and an element still visibly in the band is
            // never wrongly pruned (it would not be re-added until the next
            // crossing). Raw-rect prefilter first — clipping only shrinks a
            // rect, so a raw miss is a definite miss and skips the ancestor
            // walk.
            const raw = reg.el.getBoundingClientRect();
            if (raw.bottom < bandTop || raw.top > bandBottom) {
                intersecting.current.delete(id);
                continue;
            }
            const rect = clippedRect(reg.el, raw);
            if (!rect || rect.bottom < bandTop || rect.top > bandBottom) {
                intersecting.current.delete(id);
                continue;
            }
            const sortKey = reg.priority * 1_000_000 + rect.top;
            if (sortKey > bestKey) {
                bestKey = sortKey;
                best = reg;
            }
        }

        if (best) {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = 0;
            }
            activeIdRef.current = best.id;
            setPickedMode(best.mode);
            setSuppressed(best.mode.kind === 'hidden');
            return;
        }

        // Nothing crosses the line. Linger on the current mode and hide only once
        // the grace period elapses with nothing reclaiming it — bridges the brief
        // gaps between adjacent blocks during a continuous scroll.
        if (activeIdRef.current === null || hideTimerRef.current) return;
        hideTimerRef.current = window.setTimeout(() => {
            hideTimerRef.current = 0;
            activeIdRef.current = null;
            setPickedMode(HIDDEN);
            setSuppressed(false);
        }, HIDE_GRACE_MS);
    }, []);

    // Rebuild the in-band set from a fresh measure of every registered section,
    // then re-pick. Unlike `pickActive` (which only prunes `intersecting`), this
    // also RE-ADDS a section whose box now crosses the line — so a dropped
    // observer "entering" notification (common on a fast flick, especially
    // scrolling up past the thin band) self-heals on the next scroll frame
    // instead of leaving the island stuck hidden. Cheap: a handful of
    // getBoundingClientRect reads per rAF-throttled frame — the clip-aware
    // ancestor walk only runs for the section(s) whose raw rect is already
    // in the band.
    const syncInBand = useCallback(() => {
        const viewportHeight = window.innerHeight || 1;
        const bandHalf = ((TRIGGER_BAND_PCT / 100) * viewportHeight) / 2;
        const bandTop = TRIGGER_FRACTION * viewportHeight - bandHalf;
        const bandBottom = TRIGGER_FRACTION * viewportHeight + bandHalf;
        for (const reg of registry.current.values()) {
            if (!reg.el) {
                intersecting.current.delete(reg.id);
                continue;
            }
            const raw = reg.el.getBoundingClientRect();
            if (raw.bottom < bandTop || raw.top > bandBottom) {
                intersecting.current.delete(reg.id);
                continue;
            }
            const rect = clippedRect(reg.el, raw);
            if (!rect || rect.bottom < bandTop || rect.top > bandBottom) {
                intersecting.current.delete(reg.id);
            } else {
                intersecting.current.add(reg.id);
            }
        }
        pickActive();
    }, [pickActive]);

    // Lazily created on the first registration (client only). The two rootMargin
    // insets sum to -100%, collapsing the root to a zero-height line at
    // TRIGGER_FRACTION; with threshold 0 a section toggles `isIntersecting` as
    // that line enters or leaves its box. Observing a target also fires an
    // initial callback, so the first paint settles without a manual kick.
    const ensureObserver = useCallback((): IntersectionObserver | null => {
        if (observerRef.current || typeof window === 'undefined') {
            return observerRef.current;
        }
        const top = TRIGGER_FRACTION * 100 - TRIGGER_BAND_PCT / 2;
        const bottom = 100 - TRIGGER_FRACTION * 100 - TRIGGER_BAND_PCT / 2;
        observerRef.current = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = targets.current.get(entry.target);
                    if (!id) continue;
                    if (entry.isIntersecting) intersecting.current.add(id);
                    else intersecting.current.delete(id);
                }
                pickActive();
            },
            { rootMargin: `-${top}% 0px -${bottom}% 0px`, threshold: 0 },
        );
        return observerRef.current;
    }, [pickActive]);

    const observe = useCallback(
        (id: symbol, el: HTMLElement) => {
            const observer = ensureObserver();
            if (!observer) return;
            targets.current.set(el, id);
            observer.observe(el);
        },
        [ensureObserver],
    );

    const unobserve = useCallback((el: HTMLElement | null) => {
        if (!el) return;
        targets.current.delete(el);
        observerRef.current?.unobserve(el);
    }, []);

    const register = useCallback(
        ({ id, priority, mode, el }: RegisterInput) => {
            const prev = registry.current.get(id);
            if (prev?.el && prev.el !== el) unobserve(prev.el);
            registry.current.set(id, { id, priority, mode, el });
            if (el) observe(id, el);
        },
        [observe, unobserve],
    );

    const update = useCallback(
        (id: symbol, patch: RegistrationPatch) => {
            const current = registry.current.get(id);
            if (!current) return;
            const elChanged = 'el' in patch && patch.el !== current.el;
            if (elChanged && current.el) {
                unobserve(current.el);
                intersecting.current.delete(id);
            }
            const next = { ...current, ...patch };
            registry.current.set(id, next);
            if (elChanged && next.el) observe(id, next.el);
            // Mode / priority changes don't move the line, so re-pick directly.
            pickActive();
        },
        [observe, unobserve, pickActive],
    );

    const unregister = useCallback(
        (id: symbol) => {
            const reg = registry.current.get(id);
            if (reg?.el) unobserve(reg.el);
            registry.current.delete(id);
            intersecting.current.delete(id);
            // Deliberately leave `activeIdRef` pointing at an unregistered
            // active section: `pickActive` treats a null active id as "already
            // hidden" and would return without ever hiding the pill, stranding
            // the last mode on screen. Keeping the id routes this through the
            // same grace-then-hide path as scrolling away — so a section whose
            // `enabled` flickers at a release boundary re-claims the island
            // within the grace window instead of blinking it.
            pickActive();
        },
        [unobserve, pickActive],
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        let frame = 0;
        // Stable across the provider's life (created once), so it is safe to read
        // here and use in cleanup; the observer / timer must stay live, so those
        // are read through their refs at teardown.
        const observed = targets.current;
        // Safety net for the observer's blind spots: the last registered section
        // has nothing below it to fire an "entering" notification, and a fast
        // flick can drop enter/leave notifications for any section. A rAF-
        // throttled scroll pass re-measures every registered section against the
        // band — re-adding ones now crossing the line and pruning ones that left
        // — so neither a stale active entry nor a missed enter can outlive the
        // next scroll frame. A handful of rects per frame, not every section's.
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                syncInBand();
                syncArmed();
            });
        };
        syncArmed();
        window.addEventListener('scroll', onScroll, { passive: true });
        // Both the band geometry and the arm gate compare against
        // innerHeight, so an orientation change / resize with no scroll must
        // re-measure too — otherwise the pill can sit stale until the next
        // scroll frame.
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame) cancelAnimationFrame(frame);
            observerRef.current?.disconnect();
            observerRef.current = null;
            observed.clear();
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        };
    }, [syncInBand, syncArmed]);

    // The footer bound rides its own listener rather than the pass above: it
    // re-binds when `footerElement` resolves after mount, and folding it into
    // the observer effect would tear the IntersectionObserver down with it —
    // dropping every registered section off the observer for good.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        let frame = 0;
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                syncAtFooter();
            });
        };
        syncAtFooter();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [syncAtFooter]);

    const setOverride = useCallback(
        (mode: IslandMode | null) => setOverrideMode(mode),
        [],
    );
    const setFallback = useCallback(
        (mode: IslandMode | null) => setFallbackMode(mode),
        [],
    );

    // The override, when set, wins over whichever section the scroll picked.
    // With no scroll winner, the fallback fills in — unless a hidden-mode
    // claim is explicitly blanking the island.
    const scrolledMode =
        pickedMode.kind !== 'hidden'
            ? pickedMode
            : suppressed
              ? HIDDEN
              : (fallbackMode ?? HIDDEN);
    // The arm gate masks only the scroll-driven modes — the override (an
    // explicit user action, not a scroll position) shows regardless. The footer
    // bound and a berth-holding toast outrank even the override: nothing
    // floats over the site footer or the content gate, and nothing stacks
    // under a toast.
    const activeMode =
        atFooter || toastHoldsBerth
            ? HIDDEN
            : (overrideMode ?? (armed ? scrolledMode : HIDDEN));

    const value = useMemo(
        () => ({
            register,
            update,
            unregister,
            setOverride,
            setFallback,
            activeMode,
        }),
        [register, update, unregister, setOverride, setFallback, activeMode],
    );

    return (
        <MagicIslandContext.Provider value={value}>
            {children}
        </MagicIslandContext.Provider>
    );
};
